/**
 * What the notifications page remembers between visits, per signed-in user.
 *
 * There is no notifications table on the server: every event shown on /notifications is
 * rebuilt from the source data that already carries it (an application's stage history, a
 * CV view, a follower row, a reply on your post). That gives the *content* for free — an
 * event from two months ago is still there whether or not the app was ever opened — but it
 * leaves two things nobody else stores:
 *
 *   1. which of those events have been read, and
 *   2. which employer marks (`complete_type`) have changed since last time, since a mark
 *      is a current value rather than an event.
 *
 * Both live here, in localStorage. That is the deliberate cost of not adding a table: read
 * state is per device. Timestamps are never kept here — an event's `at` always comes from
 * the server, so a stale snapshot can never turn into a wrong "2 minutes ago".
 */

const PREFIX = "diil_notifications";

/** Enough history to keep a page's worth of rows marked read without growing forever. */
const MAX_READ_IDS = 200;

const EMPTY = {
  version: 1,
  lastSeenAt: null,
  readIds: [],
  /** { [assessmentPlayerId]: complete_type } — the candidate's own applications. */
  statusSnapshot: {},
  /** { [applicantId]: complete_type } — people on the employer's own ads. */
  applicantSnapshot: {},
  /** { [postId]: [jobId, ...] } — jobs the matching agent had already suggested. */
  suggestSnapshot: {},
};

export const storageKey = (userKey) => (userKey ? `${PREFIX}:${userKey}` : null);

/**
 * The stored state, or a blank one. Never throws: a private window, a full quota or a
 * half-written value must leave the page rendering, not blank it.
 */
export const readState = (userKey) => {
  const key = storageKey(userKey);
  if (!key) return { ...EMPTY };
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { ...EMPTY };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return { ...EMPTY };
    return {
      ...EMPTY,
      ...parsed,
      readIds: Array.isArray(parsed.readIds) ? parsed.readIds : [],
      statusSnapshot: parsed.statusSnapshot || {},
      applicantSnapshot: parsed.applicantSnapshot || {},
      suggestSnapshot: parsed.suggestSnapshot || {},
    };
  } catch {
    return { ...EMPTY };
  }
};

/**
 * Everyone currently reading this state.
 *
 * useNotifications is mounted more than once at a time — the header for its badge, the
 * panel or page for the list — and each copy holds its own React state. Without this, the
 * list marking everything seen would leave the badge in the header still showing a count,
 * because nothing told it the store had changed.
 */
const listeners = new Set();

/** Subscribe to store writes. Returns the unsubscribe function. */
export const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/** Merges a patch into the stored state and returns the result. Never throws. */
export const writeState = (userKey, patch) => {
  const key = storageKey(userKey);
  const next = { ...readState(userKey), ...patch };
  next.readIds = (next.readIds || []).slice(-MAX_READ_IDS);
  if (key) {
    try {
      localStorage.setItem(key, JSON.stringify(next));
    } catch {
      // Quota or a browser refusing storage. The page still works for this session.
    }
  }
  listeners.forEach((fn) => fn(userKey, next));
  return next;
};

/** True the first time this user opens the app on this device — nothing stored yet. */
export const isFirstRun = (state) => !state.lastSeenAt;

export const markAllSeen = (userKey) => writeState(userKey, { lastSeenAt: new Date().toISOString() });

export const markRead = (userKey, id) => {
  if (!id) return readState(userKey);
  const state = readState(userKey);
  if (state.readIds.includes(id)) return state;
  return writeState(userKey, { readIds: [...state.readIds, id] });
};

/**
 * Ids whose value moved since the snapshot was taken.
 *
 * Keys absent from the snapshot are NOT reported: a brand-new application appearing is not
 * "your status changed", it is the person having just applied — which they know. Only a
 * value that existed and then moved is news.
 */
export const changedKeys = (snapshot, next) => {
  const prev = snapshot || {};
  return Object.keys(next || {}).filter(
    (k) => prev[k] !== undefined && prev[k] !== null && prev[k] !== next[k]
  );
};

/**
 * Per post, the suggested job ids that were not there last time.
 *
 * Same rule as {@link changedKeys}: a post seen for the first time contributes nothing, or
 * writing a post would immediately notify you about the answer you are already looking at.
 */
export const newSuggestionIds = (snapshot, next) => {
  const prev = snapshot || {};
  const out = {};
  Object.entries(next || {}).forEach(([postId, ids]) => {
    const before = prev[postId];
    if (!Array.isArray(before)) return;
    const fresh = (ids || []).filter((id) => !before.includes(id));
    if (fresh.length) out[postId] = fresh;
  });
  return out;
};
